import { useEffect, useState } from 'react'

import { useHistory, useParams } from 'react-router-dom'

import { useNotifications } from '../../../contexts/notificationProvider'
import { useUserContext } from '../../../contexts/userContext'

interface PermissionWrapperProps {
  permission: string
  project_id?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper children type
  children: any
}
export const PermissionWrapper = ({
  permission,
  project_id,
  children,
}: PermissionWrapperProps) => {
  const { state: userState } = useUserContext()
  const { addNotification } = useNotifications()
  const params = useParams()
  const history = useHistory()
  const [hasRedirected, setHasRedirected] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper params type
  const effectiveProjectId = project_id || (params as any).projectId

  // Super users have access to everything
  const isSuperUser = userState.user_type === 'super'

  // Check if permissions are fully loaded (not needed for super users)
  const permissionsLoaded = isSuperUser || userState.permissions !== null
  const allLoaded = permissionsLoaded

  // Do the permission check synchronously (only if loaded) - super users always have access
  const hasAccess =
    allLoaded &&
    (isSuperUser ||
      checkUserPermissions(
        userState.permissions,
        permission,
        effectiveProjectId,
      ))

  // Handle redirect - only once, only when loaded and denied
  useEffect(() => {
    if (allLoaded && !hasAccess && !hasRedirected) {
      setHasRedirected(true)
      addNotification(
        'You do not have access to the requested page.',
        'warning',
      )
      history.push('/')
    }
  }, [
    allLoaded,
    hasAccess,
    hasRedirected,
    addNotification,
    history,
    permission,
    effectiveProjectId,
  ])

  // Not loaded yet - show nothing
  if (!allLoaded) {
    return null
  }

  // Has access - show children
  if (hasAccess) {
    return <>{children}</>
  }

  // No access - show nothing (redirect happening)
  return null
}

interface PermissionWrappedComponentProps {
  permission: string | string[]
  useOrInPermissionCheck?: boolean
  project_id?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper children type
  children: any
}
export const PermissionWrappedComponent = ({
  permission,
  useOrInPermissionCheck,
  project_id,
  children,
}: PermissionWrappedComponentProps) => {
  const { state: userState } = useUserContext()

  // Super users have access to everything
  const isSuperUser = userState.user_type === 'super'

  const _checkUserPermissions = () => {
    // Super users always have access
    if (isSuperUser) return true

    if (Array.isArray(permission)) {
      if (useOrInPermissionCheck) {
        return permission.some(perm =>
          checkUserPermissions(userState.permissions, perm, project_id),
        )
      } else {
        return permission.every(perm =>
          checkUserPermissions(userState.permissions, perm, project_id),
        )
      }
    } else {
      return checkUserPermissions(userState.permissions, permission, project_id)
    }
  }
  if (!_checkUserPermissions()) {
    return <></>
  }
  return children
}

export const checkUserPermissions = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper userPermissions type
  userPermissions: any,
  permission: string,
  project_id?: string,
) => {
  // Handle null/undefined permissions
  if (!userPermissions) {
    return false
  }

  // Extract codename (backend sometimes returns just codename, frontend uses full format)
  const permissionCodename = permission.includes('.')
    ? permission.split('.').pop() || permission
    : permission

  // Helper to check array (filter out nulls and check both formats)
  const hasPermission = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper permissionsArray element type
    permissionsArray: any[] | undefined,
    perm: string,
    codename: string,
  ): boolean => {
    if (!permissionsArray || !Array.isArray(permissionsArray)) {
      return false
    }
    // Filter out null/undefined values and check both full permission and codename
    return permissionsArray
      .filter((p): p is string => p != null && typeof p === 'string')
      .some((p: string) => p === perm || p === codename)
  }

  // Check user-level permissions
  const userLevelMatch = hasPermission(
    userPermissions.user,
    permission,
    permissionCodename,
  )

  // Check project-level permissions
  let projectLevelMatch = false
  if (
    project_id &&
    userPermissions.project &&
    userPermissions.project[project_id]
  ) {
    projectLevelMatch = hasPermission(
      userPermissions.project[project_id],
      permission,
      permissionCodename,
    )
  }

  return userLevelMatch || projectLevelMatch
}
